import { getAccessToken, getRefreshPromise } from "@/lib/api"

const ragBaseURL = (import.meta.env.VITE_RAG_API_URL ?? "").replace(/\/$/, "")

/** Progress update while the deep dive is being prepared (CEO-1). */
export interface DeepDiveProgress {
  stage: string
  text: string
  step: number
  total: number
}

type DiveStreamEvent =
  | ({ type: "status" } & DeepDiveProgress)
  | { type: "done"; deep_dive?: string }
  | { type: "error"; error?: string }

/**
 * Request the deep-dive for an assistant answer (POC 84, Thread H). The backend re-derives the
 * question/context from the stored exchange, synthesises a detailed answer grounded in the
 * answer's own chunks + linked definitions, appends reference links, persists it, and returns the
 * markdown — which the UI renders as a second assistant message bubble.
 *
 * CEO-1: we ask for the NDJSON stream (Accept: application/x-ndjson) so stage-level progress
 * events arrive while the pipeline runs; `onStatus` receives each one. The final `done` event
 * carries the markdown.
 */
export async function requestDeepDive(
  chatId: string,
  assistantTimestamp: string,
  onStatus?: (progress: DeepDiveProgress) => void,
  signal?: AbortSignal
): Promise<string> {
  const path = `/chat/${encodeURIComponent(chatId)}/deep-dive`
  const url = ragBaseURL ? `${ragBaseURL}${path}` : path
  const payload = JSON.stringify({ assistant_timestamp: assistantTimestamp })

  const send = (token: string | null) => {
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
      Accept: "application/x-ndjson",
    }
    if (token) headers.Authorization = `Bearer ${token}`
    return fetch(url, { method: "POST", headers, body: payload, signal })
  }

  let response = await send(getAccessToken())
  if (response.status === 401) {
    const newToken = await getRefreshPromise()
    if (!newToken) throw new Error("Unauthorized")
    response = await send(newToken)
  }

  if (!response.ok) {
    let message = `Deep dive request failed (${response.status})`
    try {
      const errBody = (await response.json()) as { error?: string }
      if (errBody.error) message = errBody.error
    } catch {
      // ignore parse errors
    }
    throw new Error(message)
  }

  const contentType = response.headers.get("content-type") ?? ""
  if (!contentType.includes("application/x-ndjson")) {
    // Plain-JSON fallback (older backend): original single-blob contract.
    const data = (await response.json()) as { deep_dive?: string }
    return typeof data.deep_dive === "string" ? data.deep_dive : ""
  }

  const reader = response.body?.getReader()
  if (!reader) throw new Error("Deep dive response had no body")

  const decoder = new TextDecoder()
  let buffer = ""
  let deepDive: string | null = null

  const handleLine = (line: string) => {
    if (!line) return
    const event = JSON.parse(line) as DiveStreamEvent
    if (event.type === "status") {
      onStatus?.(event)
    } else if (event.type === "done") {
      deepDive = typeof event.deep_dive === "string" ? event.deep_dive : ""
    } else if (event.type === "error") {
      throw new Error(event.error || "Deep dive failed")
    }
  }

  while (true) {
    const { value, done } = await reader.read()
    if (done) break
    buffer += decoder.decode(value, { stream: true })
    let newlineIndex = buffer.indexOf("\n")
    while (newlineIndex >= 0) {
      const line = buffer.slice(0, newlineIndex).trim()
      buffer = buffer.slice(newlineIndex + 1)
      handleLine(line)
      newlineIndex = buffer.indexOf("\n")
    }
  }
  handleLine(buffer.trim())

  if (deepDive === null) throw new Error("Deep dive stream ended before completion")
  return deepDive
}
